import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  CopyButton,
  Drawer,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useViewportSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconLayoutSidebarRightExpand, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { fetchPublicProfiles } from '../../lib/publicProfiles';
import { profileDisplayName } from '../../lib/forms';
import { findScheduleConflicts } from '../../lib/scheduleValidation';
import type { ScheduleEntryAssignment, FormSubmission, FormSubmissionAnswer, UserRole } from '../../lib/scheduleValidation';
import { AblaufplanCalendarView } from './AblaufplanCalendarView';
import { AblaufplanCategoryManager } from './AblaufplanCategoryManager';
import { AblaufplanConflictsPanel } from './AblaufplanConflictsPanel';
import { AblaufplanListView } from './AblaufplanListView';
import { AblaufplanEntryModal } from './AblaufplanEntryModal';
import type { ScheduleCategory } from './AblaufplanCategoryManager';
import type { ScheduleEntry } from './AblaufplanEntryModal';

// ── Preset categories ─────────────────────────────────────────────────────────
const PRESETS = [
  { name: 'Alle',       color: '#8B5CF6', icon: '👥', sort_order: 0 },
  { name: 'Chor',       color: '#EAB308', icon: '🎵', sort_order: 1 },
  { name: 'Tanz',       color: '#22C55E', icon: '💃', sort_order: 2 },
  { name: 'Schauspiel', color: '#3B82F6', icon: '🎭', sort_order: 3 },
];

// ── Date helpers ──────────────────────────────────────────────────────────────
function getDayRange(start: string, end: string): string[] {
  if (!start || !end) return [];
  const days: string[] = [];
  const cur = dateToLocal(start);
  const last = dateToLocal(end);
  while (cur <= last && days.length <= 60) {
    days.push(dateStrOf(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function fmtDay(dateStr: string, idx: number): string {
  const d = dateToLocal(dateStr);
  return `Tag ${idx + 1} · ${d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}`;
}

function dateToLocal(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function dateStrOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Normalises what Mantine DatePickerInput returns — either a Date object or an ISO string */
function pickDate(val: unknown): string {
  if (val instanceof Date) return dateStrOf(val);
  if (typeof val === 'string' && val.length >= 10) return val.slice(0, 10);
  return '';
}

type ScheduleStatus = 'draft' | 'published';
type ScheduleSettings = {
  form_id: string;
  event_start_date: string | null;
  event_end_date: string | null;
  status: ScheduleStatus;
  title: string | null;
  preview_for_admins: boolean;
};
type Props = { formId: string; formSlug?: string };

export function AblaufplanTab({ formId, formSlug }: Props) {
  const qc = useQueryClient();
  const { width } = useViewportSize();
  const isMobile = width > 0 && width < 1000;

  const [selectedDate, setSelectedDate] = useState('');
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [highlightedEntryIds, setHighlightedEntryIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);

  // ── Copy day modal state ─────────────────────────────────────────────────
  const [copyModal, setCopyModal] = useState(false);
  const [copySource, setCopySource] = useState('');
  const [copyTargets, setCopyTargets] = useState<string[]>([]);

  // ── Queries ───────────────────────────────────────────────────────────────
  const categoriesQ = useQuery<ScheduleCategory[]>({
    queryKey: ['schedule-categories', formId],
    queryFn: async () => {
      const { data, error } = await supabase.from('schedule_categories').select('*').eq('form_id', formId).order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const entriesQ = useQuery<ScheduleEntry[]>({
    queryKey: ['schedule-entries', formId],
    queryFn: async () => {
      const { data, error } = await supabase.from('schedule_entries').select('*').eq('form_id', formId).order('entry_date').order('start_time');
      if (error) throw error;
      return data;
    },
  });

  const settingsQ = useQuery<ScheduleSettings | null>({
    queryKey: ['schedule-settings', formId],
    queryFn: async () => {
      const { data } = await supabase.from('schedule_settings').select('*').eq('form_id', formId).maybeSingle();
      return data as ScheduleSettings | null;
    },
  });

  // For conflict detection
  const assignmentsQ = useQuery<ScheduleEntryAssignment[]>({
    queryKey: ['schedule-all-assignments', formId],
    queryFn: async () => {
      const entryIds = (entriesQ.data ?? []).map((e) => e.id);
      if (entryIds.length === 0) return [];
      const { data, error } = await supabase.from('schedule_entry_assignments').select('*').in('entry_id', entryIds);
      if (error) throw error;
      return data as ScheduleEntryAssignment[];
    },
    enabled: (entriesQ.data?.length ?? 0) > 0,
  });

  const submissionsQ = useQuery<FormSubmission[]>({
    queryKey: ['form-submissions-conflict', formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('form_submissions')
        .select('id, form_id, user_id')
        .eq('form_id', formId);
      if (error) throw error;
      const rows = (data ?? []) as Array<{ id: string; form_id: string; user_id: string | null }>;

      // Enrich with display names via publicProfiles
      const userIds = rows.map((r) => r.user_id).filter(Boolean) as string[];
      const profiles = userIds.length > 0 ? await fetchPublicProfiles(userIds) : [];
      const profileById = new Map(profiles.map((p) => [p.id, p]));

      return rows.map((row, idx): FormSubmission => {
        const profile = row.user_id ? profileById.get(row.user_id) : undefined;
        const participant_name = profile
          ? profileDisplayName(profile as Record<string, unknown>) || `Teilnehmer ${idx + 1}`
          : `Teilnehmer ${idx + 1}`;
        return {
          id: row.id,
          form_id: row.form_id,
          user_id: row.user_id ?? '',
          participant_name,
        };
      });
    },
  });

  const userRolesQ = useQuery<UserRole[]>({
    queryKey: ['all-user-roles'],
    queryFn: async () => {
      const { data, error } = await supabase.from('user_roles').select('user_id, role_id');
      if (error) throw error;
      return (data ?? []) as UserRole[];
    },
  });

  const answersQ = useQuery<FormSubmissionAnswer[]>({
    queryKey: ['form-submission-answers-conflict', formId],
    queryFn: async () => {
      const submissionIds = (submissionsQ.data ?? []).map((s) => s.id);
      if (submissionIds.length === 0) return [];
      const { data, error } = await supabase
        .from('form_submission_answers')
        .select('submission_id, field_key, value_text, value_json')
        .in('submission_id', submissionIds);
      if (error) throw error;
      return (data ?? []) as FormSubmissionAnswer[];
    },
    enabled: (submissionsQ.data?.length ?? 0) > 0,
  });

  // ── Conflict computation ──────────────────────────────────────────────────
  const conflicts = useMemo(() => {
    if (!entriesQ.data || !assignmentsQ.data) return [];
    return findScheduleConflicts(
      entriesQ.data,
      assignmentsQ.data,
      submissionsQ.data ?? [],
      answersQ.data ?? [],
      userRolesQ.data ?? [],
    );
  }, [entriesQ.data, assignmentsQ.data, submissionsQ.data, answersQ.data]);

  // ── Auto-seed preset categories when dates are set and no categories exist ──
  useEffect(() => {
    const s = settingsQ.data?.event_start_date;
    const e = settingsQ.data?.event_end_date;
    if (categoriesQ.data && categoriesQ.data.length === 0 && s && e) {
      supabase.from('schedule_categories')
        .insert(PRESETS.map((p) => ({ ...p, form_id: formId, is_default: true })))
        .then(() => qc.invalidateQueries({ queryKey: ['schedule-categories', formId] }));
    }
  }, [categoriesQ.data, settingsQ.data, formId, qc]);

  useEffect(() => {
    if (settingsQ.data?.event_start_date && !selectedDate) {
      setSelectedDate(settingsQ.data.event_start_date);
    }
  }, [settingsQ.data, selectedDate]);

  // ── Save settings ─────────────────────────────────────────────────────────
  const saveSettings = useMutation({
    mutationFn: async ({ start, end, title }: { start: string; end: string; title?: string }) => {
      const { error } = await supabase.from('schedule_settings').upsert({
        form_id: formId,
        event_start_date: start || null,
        event_end_date: end || null,
        ...(title !== undefined ? { title: title || null } : {}),
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-settings', formId] }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  // ── Status toggle ─────────────────────────────────────────────────────────
  const saveStatus = useMutation({
    mutationFn: async (status: ScheduleStatus) => {
      const { error } = await supabase.from('schedule_settings').upsert({ form_id: formId, status });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-settings', formId] }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  // ── Copy day ──────────────────────────────────────────────────────────────
  const copyDayMutation = useMutation({
    mutationFn: async () => {
      if (!copySource || copyTargets.length === 0) throw new Error('Quelle und Ziele auswählen.');
      const sourceEntries = (entriesQ.data ?? []).filter((e) => e.entry_date === copySource);
      for (const targetDay of copyTargets) {
        for (const entry of sourceEntries) {
          const { data: newRow, error } = await supabase
            .from('schedule_entries')
            .insert({
              form_id: entry.form_id, category_id: entry.category_id,
              entry_date: targetDay, start_time: entry.start_time, end_time: entry.end_time,
              title: entry.title, description: entry.description, location: entry.location,
              is_app_visible: entry.is_app_visible, sort_order: entry.sort_order,
            })
            .select('id').single();
          if (error) throw error;
          const { data: assignments } = await supabase
            .from('schedule_entry_assignments').select('*').eq('entry_id', entry.id);
          if (assignments && assignments.length > 0) {
            await supabase.from('schedule_entry_assignments').insert(
              assignments.map((a) => ({
                entry_id: newRow.id, assignment_type: a.assignment_type,
                submission_id: a.submission_id, rule_field_key: a.rule_field_key,
                rule_operator: a.rule_operator, rule_value: a.rule_value,
              }))
            );
          }
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-entries', formId] });
      setCopyModal(false);
      setCopyTargets([]);
      notifications.show({ color: 'green', title: 'Tag kopiert', message: `${copyTargets.length} Tag(e) wurden befüllt.` });
    },
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  // ── Delete entire Ablaufplan ──────────────────────────────────────────────
  const deleteAblaufplanMutation = useMutation({
    mutationFn: async () => {
      if (!window.confirm('Den gesamten Ablaufplan (alle Einträge, Kategorien und Einstellungen) für dieses Formular wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
      // Cascade via FK: deleting entries removes assignments automatically
      await supabase.from('schedule_entries').delete().eq('form_id', formId);
      await supabase.from('schedule_categories').delete().eq('form_id', formId);
      await supabase.from('schedule_settings').delete().eq('form_id', formId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule-entries', formId] });
      qc.invalidateQueries({ queryKey: ['schedule-categories', formId] });
      qc.invalidateQueries({ queryKey: ['schedule-settings', formId] });
      setSelectedDate('');
      notifications.show({ color: 'green', title: 'Ablaufplan entfernt', message: 'Alle Daten wurden gelöscht.' });
    },
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  // ── Delete entry ──────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (entry: ScheduleEntry) => {
      if (!window.confirm(`Eintrag "${entry.title}" wirklich löschen?`)) return;
      const { error } = await supabase.from('schedule_entries').delete().eq('id', entry.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-entries', formId] }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  const currentStatus: ScheduleStatus = settingsQ.data?.status ?? 'draft';
  const isPublished = currentStatus === 'published';
  const days = getDayRange(settingsQ.data?.event_start_date ?? '', settingsQ.data?.event_end_date ?? '');
  const startDate = settingsQ.data?.event_start_date ?? '';
  const endDate = settingsQ.data?.event_end_date ?? '';
  const isLoading = categoriesQ.isLoading || entriesQ.isLoading || settingsQ.isLoading;

  const activeView = isMobile ? 'list' : view;
  const selectedDayEntries = useMemo(
    () => (entriesQ.data ?? []).filter((entry) => entry.entry_date === selectedDate),
    [entriesQ.data, selectedDate],
  );
  const selectedEntries = useMemo(
    () => selectedDayEntries.filter((entry) => selectedEntryIds.includes(entry.id)),
    [selectedDayEntries, selectedEntryIds],
  );

  useEffect(() => {
    setSelectedEntryIds((prev) => prev.filter((id) => selectedDayEntries.some((entry) => entry.id === id)));
  }, [selectedDayEntries]);

  const toggleEntrySelection = (entryId: string) => {
    setSelectedEntryIds((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId]
    );
  };

  const bulkShiftMutation = useMutation({
    mutationFn: async (minutesDelta: number) => {
      if (selectedEntries.length === 0) return;
      const invalidEntry = selectedEntries.find((entry) => {
        const nextStart = timeToMinutes(entry.start_time) + minutesDelta;
        const nextEnd = timeToMinutes(entry.end_time) + minutesDelta;
        return nextStart < 0 || nextEnd > (24 * 60) || nextEnd <= nextStart;
      });
      if (invalidEntry) {
        throw new Error(`„${invalidEntry.title}" würde ausserhalb des Tages liegen.`);
      }

      for (const entry of selectedEntries) {
        const result = await supabase.rpc('update_schedule_entry', {
          p_id: entry.id,
          p_expected_version: entry.version,
          p_title: entry.title,
          p_description: entry.description,
          p_location: entry.location,
          p_entry_date: entry.entry_date,
          p_start_time: minutesToTime(timeToMinutes(entry.start_time) + minutesDelta),
          p_end_time: minutesToTime(timeToMinutes(entry.end_time) + minutesDelta),
          p_category_id: entry.category_id,
          p_sort_order: entry.sort_order,
          p_is_app_visible: entry.is_app_visible,
          p_visible_until: entry.visible_until,
        });
        const data = result.data as { ok: boolean; error?: string } | null;
        if (!data?.ok) {
          throw new Error(
            data?.error === 'conflict'
              ? `„${entry.title}" wurde zwischenzeitlich geändert.`
              : (data?.error ?? `„${entry.title}" konnte nicht verschoben werden.`),
          );
        }
      }
    },
    onSuccess: (_, minutesDelta) => {
      qc.invalidateQueries({ queryKey: ['schedule-entries', formId] });
      notifications.show({
        color: 'teal',
        title: 'Zeiten verschoben',
        message: `${selectedEntries.length} Eintrag/Einträge um ${minutesDelta > 0 ? '+' : ''}${minutesDelta} Minuten verschoben.`,
      });
    },
    onError: (error: Error) => {
      notifications.show({ color: 'red', title: 'Massenbearbeitung fehlgeschlagen', message: error.message });
    },
  });

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Ablaufplan wird geladen…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="sm" mt="sm">

      {/* ── Draft / Publish toggle — nur wenn Zeitraum gesetzt ───────────── */}
      {startDate && endDate && <Alert color={isPublished ? 'green' : 'yellow'} variant="light" p="sm" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Group gap="xs">
              <Badge color={isPublished ? 'green' : 'yellow'} size="sm">
                {isPublished ? '✅ Veröffentlicht' : '📝 Entwurf'}
              </Badge>
              <Text size="xs" c="dimmed">
                {isPublished
                  ? 'Alle Teilnehmer sehen den Ablaufplan.'
                  : 'Nur Admins sehen den Ablaufplan in der App.'}
              </Text>
              {!isPublished && (
                <Tooltip label="Wenn AN: Admins sehen den Entwurf in der App. Wenn AUS: nichts sichtbar." position="bottom" multiline w={240}>
                  <Switch
                    size="sm"
                    color="yellow"
                    label="In App anzeigen (Entwurf)"
                    checked={settingsQ.data?.preview_for_admins ?? true}
                    onChange={async (e) => {
                      const val = e.currentTarget.checked;
                      await supabase.from('schedule_settings').upsert({
                        form_id: formId,
                        preview_for_admins: val,
                        updated_at: new Date().toISOString(),
                      });
                      qc.invalidateQueries({ queryKey: ['schedule-settings', formId] });
                    }}
                    labelPosition="right"
                    styles={{ label: { fontSize: 12 } }}
                  />
                </Tooltip>
              )}
              {isPublished && formSlug && (
                <Anchor
                  href={`${window.location.origin}/ablaufplan/${formSlug}`}
                  target="_blank"
                  size="xs"
                >
                  {window.location.origin}/ablaufplan/{formSlug} ↗
                </Anchor>
              )}
            </Group>
          </Stack>
          <Group gap="sm" wrap="nowrap" align="center">
            {isPublished && formSlug && (
              <CopyButton value={`${window.location.origin}/ablaufplan/${formSlug}`}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Link kopiert!' : 'Web-Link kopieren'} position="left">
                    <ActionIcon variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy} size="md">
                      {copied ? '✅' : '🔗'}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
            <Tooltip label={isPublished ? 'Zurück auf Entwurf' : 'Für alle veröffentlichen'} position="left">
              <Switch
                checked={isPublished}
                size="md"
                color="green"
                label={isPublished ? 'In App sichtbar' : 'Veröffentlichen'}
                onChange={(e) => saveStatus.mutate(e.currentTarget.checked ? 'published' : 'draft')}
                disabled={saveStatus.isPending}
              />
            </Tooltip>
            <Tooltip label="Gesamten Ablaufplan löschen" position="left">
              <ActionIcon
                color="red"
                variant="subtle"
                size="md"
                loading={deleteAblaufplanMutation.isPending}
                onClick={() => deleteAblaufplanMutation.mutate()}
              >
                🗑
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Alert>}

      {/* ── Conflict panel ────────────────────────────────────────────────── */}
      {(entriesQ.data?.length ?? 0) > 0 && (
        <AblaufplanConflictsPanel
          conflicts={conflicts}
          onHighlightEntries={setHighlightedEntryIds}
        />
      )}

      {/* ── Schedule title ────────────────────────────────────────────────── */}
      <TextInput
        label="Ablaufplan-Titel"
        description="Wird in der App als Überschrift angezeigt"
        placeholder="z.B. Musikal 2026 – Probenwoche"
        defaultValue={settingsQ.data?.title ?? ''}
        onBlur={(e) =>
          saveSettings.mutate({ start: startDate, end: endDate, title: e.currentTarget.value })
        }
      />

      {/* ── Event date range ──────────────────────────────────────────────── */}
      <Stack gap={4}>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
          Veranstaltungszeitraum
        </Text>
        <Group gap="sm" align="flex-end">
          <DatePickerInput
            size="xs" label="Von" placeholder="Startdatum"
            value={startDate ? dateToLocal(startDate) : null}
            onChange={(val: unknown) => {
              const s = pickDate(val);
              saveSettings.mutate({ start: s, end: endDate });
              if (s && (!selectedDate || selectedDate < s)) setSelectedDate(s);
            }}
            style={{ width: 150 }} locale="de"
          />
          <DatePickerInput
            size="xs" label="Bis" placeholder="Enddatum"
            value={endDate ? dateToLocal(endDate) : null}
            onChange={(val: unknown) => {
              saveSettings.mutate({ start: startDate, end: pickDate(val) });
            }}
            minDate={startDate ? dateToLocal(startDate) : (undefined as any)}
            style={{ width: 150 }} locale="de"
          />
        </Group>
      </Stack>

      {/* ── Categories ────────────────────────────────────────────────────── */}
      <CategoryStrip
        formId={formId}
        categories={categoriesQ.data ?? []}
        onRefresh={() => qc.invalidateQueries({ queryKey: ['schedule-categories', formId] })}
      />

      {/* ── Day tabs + actions ────────────────────────────────────────────── */}
      {days.length > 0 && (
        <Group gap="sm" align="center" wrap="nowrap">
          <ScrollArea type="scroll" scrollbarSize={4} style={{ flex: 1 }}>
            <Group gap={6} wrap="nowrap" pb={4}>
              {days.map((day, i) => (
                <Button
                  key={day} size="xs"
                  variant={selectedDate === day ? 'filled' : 'default'}
                  onClick={() => setSelectedDate(day)}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {fmtDay(day, i)}
                </Button>
              ))}
            </Group>
          </ScrollArea>

          {/* Copy day button */}
          <Tooltip label="Tag kopieren" position="left">
            <ActionIcon
              variant="default" size="md"
              onClick={() => { setCopySource(selectedDate); setCopyModal(true); }}
              disabled={!selectedDate || (entriesQ.data ?? []).filter((e) => e.entry_date === selectedDate).length === 0}
            >
              📋
            </ActionIcon>
          </Tooltip>

          {/* Add entry */}
          <Button size="xs" onClick={() => { setEditingEntry(null); setModalOpen(true); }}>
            + Eintrag
          </Button>
        </Group>
      )}

      {selectedDate && (
        <Stack gap="xs">
          <Group gap="xs" wrap="wrap" justify="space-between">
            <Group gap="xs" wrap="wrap">
              <Button
                size="xs"
                variant={selectionMode ? 'filled' : 'light'}
                onClick={() => {
                  setSelectionMode((prev) => {
                    const next = !prev;
                    if (!next) setSelectedEntryIds([]);
                    return next;
                  });
                }}
              >
                {selectionMode ? 'Markieren beenden' : 'Mehrere markieren'}
              </Button>
              {selectionMode && (
                <>
                  <Button
                    size="xs"
                    variant="default"
                    disabled={selectedDayEntries.length === 0}
                    onClick={() => setSelectedEntryIds(selectedDayEntries.map((entry) => entry.id))}
                  >
                    Alle
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    disabled={selectedEntryIds.length === 0}
                    onClick={() => setSelectedEntryIds([])}
                  >
                    Auswahl leeren
                  </Button>
                </>
              )}
            </Group>
            {selectionMode && (
              <Text size="sm" c="dimmed">
                {selectedEntryIds.length} markiert
              </Text>
            )}
          </Group>

          {selectionMode && (
            <Group gap="xs" wrap="wrap">
              {[-60, -30, -15, 15, 30, 60].map((minutes) => (
                <Button
                  key={minutes}
                  size="xs"
                  variant="light"
                  disabled={selectedEntryIds.length === 0}
                  loading={bulkShiftMutation.isPending}
                  onClick={() => bulkShiftMutation.mutate(minutes)}
                >
                  {minutes > 0 ? `+${minutes}` : String(minutes)} Min
                </Button>
              ))}
            </Group>
          )}
        </Stack>
      )}

      {/* ── View toggle (desktop only) ────────────────────────────────────── */}
      {!isMobile && selectedDate && (
        <Group justify="flex-end">
          <SegmentedControl
            size="xs" value={activeView}
            onChange={(v) => setView(v as 'calendar' | 'list')}
            data={[{ label: '📅 Kalender', value: 'calendar' }, { label: '📋 Liste', value: 'list' }]}
          />
        </Group>
      )}

      {/* ── No date range ─────────────────────────────────────────────────── */}
      {!startDate && (
        <Text c="dimmed" size="sm" ta="center" py="lg">
          Lege zuerst den Veranstaltungszeitraum fest.
        </Text>
      )}

      {/* ── Main content ──────────────────────────────────────────────────── */}
      {selectedDate && activeView === 'calendar' && (
        <AblaufplanCalendarView
          entries={entriesQ.data ?? []}
          categories={categoriesQ.data ?? []}
          selectedDate={selectedDate}
          formId={formId}
          highlightedEntryIds={highlightedEntryIds}
          selectionMode={selectionMode}
          selectedEntryIds={selectedEntryIds}
          onToggleSelection={toggleEntrySelection}
          onOpenFullEdit={(entry) => { setEditingEntry(entry); setModalOpen(true); }}
        />
      )}

      {selectedDate && activeView === 'list' && (
        <AblaufplanListView
          entries={selectedDayEntries}
          categories={categoriesQ.data ?? []}
          formId={formId}
          highlightedEntryIds={highlightedEntryIds}
          selectionMode={selectionMode}
          selectedEntryIds={selectedEntryIds}
          onToggleSelection={toggleEntrySelection}
          onEdit={(e) => { setEditingEntry(e); setModalOpen(true); }}
          onDelete={(e) => deleteMutation.mutate(e)}
        />
      )}

      {/* ── Entry modal ───────────────────────────────────────────────────── */}
      <AblaufplanEntryModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        formId={formId}
        entry={editingEntry}
        categories={categoriesQ.data ?? []}
      />

      {/* ── Copy day modal ────────────────────────────────────────────────── */}
      <Modal opened={copyModal} onClose={() => setCopyModal(false)} title="Tag kopieren" size="sm">
        <Stack>
          <Select
            label="Quell-Tag"
            data={days.map((d, i) => ({ value: d, label: fmtDay(d, i) }))}
            value={copySource}
            onChange={(v) => setCopySource(v ?? '')}
          />
          <Text size="sm" fw={600}>Ziel-Tage (Mehrfachauswahl)</Text>
          <Stack gap={6}>
            {days
              .filter((d) => d !== copySource)
              .map((d) => (
                <Checkbox
                  key={d}
                  label={fmtDay(d, days.indexOf(d))}
                  checked={copyTargets.includes(d)}
                  onChange={(e) =>
                    setCopyTargets((prev) =>
                      e.currentTarget.checked ? [...prev, d] : prev.filter((x) => x !== d)
                    )
                  }
                />
              ))}
          </Stack>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCopyModal(false)}>Abbrechen</Button>
            <Button
              onClick={() => copyDayMutation.mutate()}
              loading={copyDayMutation.isPending}
              disabled={copyTargets.length === 0}
            >
              Kopieren ({copyTargets.length} Tag{copyTargets.length !== 1 ? 'e' : ''})
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ── CategoryStrip ─────────────────────────────────────────────────────────────
function CategoryStrip({
  formId, categories, onRefresh,
}: { formId: string; categories: ScheduleCategory[]; onRefresh: () => void }) {
  const [showManager, setShowManager] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);

  async function remove(cat: ScheduleCategory) {
    const { count } = await supabase
      .from('schedule_entries').select('id', { count: 'exact', head: true }).eq('category_id', cat.id);
    if ((count ?? 0) > 0 && !window.confirm(`"${cat.name}" wird von ${count} Eintrag/Einträgen genutzt. Trotzdem löschen?`)) return;
    await supabase.from('schedule_categories').delete().eq('id', cat.id);
    onRefresh();
  }

  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="wrap">
        {categories.map((cat) => (
          <Badge
            key={cat.id} size="md"
            style={{ background: cat.color + '33', border: `1px solid ${cat.color}`, color: cat.color, paddingRight: 4, cursor: 'pointer' }}
            onDoubleClick={() => {
              setEditCategoryId(cat.id);
              setShowManager(true);
            }}
            rightSection={
              <ActionIcon size={14} variant="transparent" style={{ color: cat.color }} onClick={() => remove(cat)}>
                <IconX size={10} />
              </ActionIcon>
            }
          >
            {cat.icon ? `${cat.icon} ` : ''}{cat.name}
          </Badge>
        ))}
        <Button
          size="xs"
          variant="light"
          leftSection={<IconLayoutSidebarRightExpand size={14} />}
          onClick={() => setShowManager(true)}
        >
          Kategorien
        </Button>
      </Group>
      <Drawer
        opened={showManager}
        onClose={() => {
          setShowManager(false);
          setEditCategoryId(null);
        }}
        title="Kategorien"
        position="right"
        size="sm"
      >
        <AblaufplanCategoryManager
          formId={formId}
          editCategoryId={editCategoryId}
          onEditCategoryHandled={() => setEditCategoryId(null)}
        />
      </Drawer>
    </Stack>
  );
}
